import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useLang } from '../i18n/context';
import { INSTITUTIONS, fetchInstitutionHoldings } from '../utils/parse13f';
import { buildSectorDistribution, getConcentrationBadge, getTopHoldingsConcentration } from '../utils/portfolioMetrics';
import './InstitutionListPage.css';
import '../styles/pro-page.css';

function formatChangeSummary(summary, lang) {
  if (!summary) return null;
  const labels = lang === 'zh'
    ? { new: '新增', increased: '增持', decreased: '减持', sold: '清仓' }
    : { new: 'New', increased: 'Added', decreased: 'Trimmed', sold: 'Sold' };
  return `${summary.new} ${labels.new} · ${summary.increased} ${labels.increased} · ${summary.decreased} ${labels.decreased} · ${summary.sold} ${labels.sold}`;
}

export default function InstitutionListPage() {
  const navigate = useNavigate();
  const { lang, t } = useLang();
  const [instData, setInstData] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    let cancelled = false;
    async function loadAll() {
      setLoading(true);
      const results = await Promise.allSettled(
        INSTITUTIONS.map(async (inst) => {
          const data = await fetchInstitutionHoldings(inst);
          return { inst, data };
        })
      );
      if (!cancelled) {
        setInstData(
          results
            .filter((r) => r.status === 'fulfilled' && r.value.data)
            .map((r) => r.value)
        );
        setLoading(false);
      }
    }
    loadAll();
    return () => { cancelled = true; };
  }, []);

  return (
    <main className="pro-page">
      <div className="pro-page-inner">
        <header className="pro-hero">
          <h1>{t('instList.title')}</h1>
          <span className="pro-hero-desc">{t('instList.desc')}</span>
        </header>

        {loading ? (
          <div className="inst-list-loading">{t('instList.loading')}</div>
        ) : (
          <div className="inst-list-grid">
            {instData.map(({ inst, data }) => {
              const displayName = lang === 'zh' ? inst.name : inst.nameEn;
              const description = lang === 'zh' ? inst.desc : inst.descEn;
              const top3 = data.holdings.slice(0, 3);
              const concentration = getTopHoldingsConcentration(data.holdings);
              const concentrationBadge = getConcentrationBadge(concentration, lang);
              const sectors = buildSectorDistribution(data.holdings, lang).slice(0, 3);
              const changeSummary = formatChangeSummary(data.changeSummary, lang);
              const qoqChange = data.trend?.qoqChangePct;
              const totalValue = data.totalValue > 0
                ? `$${(data.totalValue / 1e6).toFixed(1)}B`
                : '—';

              return (
                <div
                  key={inst.cik}
                  className="inst-list-card"
                  onClick={() => navigate(`/institution/${inst.cik}`)}
                >
                  <div className="inst-list-card-head">
                    <div className="inst-list-card-short">{inst.short}</div>
                    <div>
                      <div className="inst-list-card-name">{displayName}</div>
                      {description && (
                        <div className="inst-list-card-desc">{description}</div>
                      )}
                      <div className="inst-list-card-date">
                        {data.filingDate
                          ? `${t('instList.filingDate')}：${data.filingDate}`
                          : ''}
                      </div>
                    </div>
                  </div>

                  <div className="inst-list-card-meta">
                    <span className="inst-list-card-total">
                      {t('instList.totalValue')}：{totalValue}
                    </span>
                    <span className="inst-list-card-count">
                      {t('instList.holdingsCount')}：{data.holdings.length}
                    </span>
                    <span className={`inst-list-card-concentration ${concentrationBadge.className}`}>
                      {concentrationBadge.label} {concentration.toFixed(1)}%
                    </span>
                    {qoqChange != null && (
                      <span className={`inst-list-card-qoq ${qoqChange >= 0 ? 'positive' : 'negative'}`}>
                        QoQ {qoqChange >= 0 ? '+' : ''}{qoqChange.toFixed(1)}%
                      </span>
                    )}
                  </div>

                  {changeSummary && (
                    <div className="inst-list-card-changes">{changeSummary}</div>
                  )}

                  {sectors.length > 0 && (
                    <div className="inst-list-card-sectors">
                      {sectors.map((sector) => (
                        <span key={sector.key} className={`inst-list-card-sector sector-${sector.key}`}>
                          {sector.label} {sector.weight.toFixed(1)}%
                        </span>
                      ))}
                    </div>
                  )}

                  <div className="inst-list-card-top3">
                    <div className="inst-list-card-top3-label">{t('instList.topHoldings')}</div>
                    {top3.map((h, i) => (
                      <div key={h.cusip + i} className="inst-list-card-holding">
                        <span className="inst-list-card-holding-name">{h.nameOfIssuer}</span>
                        <span className="inst-list-card-holding-weight">{h.weight}%</span>
                      </div>
                    ))}
                  </div>

                  <div className="inst-list-card-arrow">
                    {t('instList.viewDetail')} →
                  </div>
                </div>
              );
            })}
          </div>
        )}

        <div className="inst-list-source">{t('instList.source')}</div>
      </div>
    </main>
  );
}
